import { useTranslation } from "react-i18next";
import {
    TEXT_FIELD_TYPE,
    DATE_RANGE_FIELD_TYPE,
    AUTOCOMPLETE_FIELD_TYPE,
    NUMERIC_RANGE_FIELD_TYPE,
    BOOLEAN_FIELD_TYPE,
} from "../../helpers/constants";
import { IconButton } from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import PaymentsIcon from '@mui/icons-material/Payments';
import { useDialogBetter } from "../../hooks/useDialogBetter";
import { useTransfersInvestmentBillsFilteringData } from "../../services/bankingService";
import {ReservationTransfersForAgreementDialog} from "./ReservationTransfersForAgreementDialog";
import { TRANSFER_STATUS_KIND_PAID_CANCELED, TRANSFER_STATUS_KIND_REBOOKED } from "../../schemas/banking";
import InfoIcon from "@mui/icons-material/Info";
import { formatCurrency } from "../../components/common/PriceDisplay";
import DateService from "../../services/dateService";
import { AgreementPassportButton } from "../AgreementsPage/AgreementPassportDialog/AgreementPassportButton";
import SelectFieldService from "../../services/selectFieldService";

export const TRANSFER_TABLE_NAME = "transfers_table";

export const useTransferTableFieldsConfig = ({isForInvocingPage}={isForInvocingPage:false}) => {
    const { t } = useTranslation();

    const { data: filteringData } = useTransfersInvestmentBillsFilteringData()

    const { openDialog: openReservationTransfersForAgreementDialog } = useDialogBetter();
    
    const handleClickOpenReservationTransfersForAgreementDialogInEditMode = (e, agreementId) => {
        e.stopPropagation();
        e.preventDefault();
        openReservationTransfersForAgreementDialog({
            Component: ReservationTransfersForAgreementDialog,
            componentProps: {
            agreementId: agreementId,
            readOnly:false,
            isReturningTransfersDialog: false
            },
        });
    };

    const handleClickOpenReturnTransfersForAgreementDialog = (e, agreementId) => {
        e.stopPropagation();
        e.preventDefault();
        openReservationTransfersForAgreementDialog({
            Component: ReservationTransfersForAgreementDialog,
            componentProps: {
            agreementId: agreementId,
            readOnly:true,
            isReturningTransfersDialog: true,
            },
        });
    };

    const openReservationTransfersForAgreementDialogInReadOnlyMode = (e, agreementId) => {
        e.stopPropagation();
        e.preventDefault();
        openReservationTransfersForAgreementDialog({
            Component: ReservationTransfersForAgreementDialog,
            componentProps: {
            agreementId: agreementId,
            readOnly:true,
            isReturningTransfersDialog: false,
            isForInvoicingPage: isForInvocingPage,
            },
        });
    };

    const fieldConfigs = [
        {
            name: "transfer_agreement_nr",
            getValueCallback: (rowData) => rowData.transfer_agreement_nr,
            label: t("bankings.transfer_agreement_nr"),
            filterType: TEXT_FIELD_TYPE,
            // sortable: true
        },
        {
            name: "agreement_customer",
            getValueCallback: (rowData) => (
                rowData.agreement_customer && rowData.agreement_customer_has_multiple_agreements ? 
                <>
                    {rowData.agreement_customer}
                    <InfoIcon color="primary" fontSize="small"/>
                </> : 
                rowData.agreement_customer
            ),
            label: t("bankings.agreement_customer"),
            filterType: TEXT_FIELD_TYPE,
        },
        {
            name: "agreement_company",
            getValueCallback: (rowData) => (
                rowData.agreement_company && rowData.agreement_customer_has_multiple_agreements ? 
                <>
                    {rowData.agreement_company}
                    <InfoIcon color="primary" fontSize="small" />
                </> : 
                rowData.agreement_company
            ),
            label: t("bankings.agreement_company"),
            filterType: TEXT_FIELD_TYPE,
        },
        {
            name: "agreement_product",
            getValueCallback: (rowData) => rowData.agreement_product,
            label: t("bankings.agreement_product"),
            filterType: TEXT_FIELD_TYPE,
        },
        {
            name: "agrepay_total_price",
            getValueCallback: (rowData) => formatCurrency(rowData.agrepay_total_price),
            label: t("bankings.agrepay_total_price"),
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
            minWidth: 120,
            width: 120,
        },
        {
            name: "agrepay_due_date",
            getValueCallback: (rowData) => rowData.agrepay_due_date,
            label: t("bankings.agrepay_due_date"),
            filterType: DATE_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                style: { minWidth: "100px" },
            }),
            sortable: true,
        },
        {
            name: "transfer_date",
            getValueCallback: (rowData) => rowData.transfer_date,
            label: t("bankings.last_transfer_date"),
            filterType: DATE_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                style: { minWidth: "100px" },
            }),
            sortable: true,
        },
        {
            name: "transfer_status",
            getValueCallback: (rowData) => (
                <span style={{ 
                    color: rowData.transfer_status_color, 
                }}>
                    {rowData.transfer_status_name}
                </span>
            ),
            other_export_field_name: "transfer_status_name",
            label: t("bankings.transfer_status_name"),
            filterType: AUTOCOMPLETE_FIELD_TYPE,
            fieldConfig: {
                optionLabelKey: "transfer_status_name",
                options: filteringData?.transfer_status,
                multiple: true,
                isObjectOption: true,
                disableClearable: true
            },
            sortable: true,
        },
        {
            name: "transfer_is_invoiced",
            getValueCallback: (rowData) =>  SelectFieldService.getLabelOptionsTrueOrFalse(rowData.transfer_is_invoiced),
            label: t("bankings.transfer_is_invoiced"),
            filterType: BOOLEAN_FIELD_TYPE,
            sortable: false,
        },
        // {
        //     name: "created",
        //     getValueCallback: (rowData) => DateService.convertDatetimeFromBackendToFormatYYYYMMDD_hhmmss(rowData.created),
        //     label: t("bankings.transfer_created"),
        //     filterType: DATE_RANGE_FIELD_TYPE,
        //     getFilterFieldConfigCallback: (filteringData) => ({
        //         style: { minWidth: "100px" },
        //     }),
        //     sortable: true,
        // },
        {
            name: "edit",
            getValueCallback: (rowData) => (
                isForInvocingPage || rowData.transfer_status_id === TRANSFER_STATUS_KIND_REBOOKED.id ? 
                <IconButton
                    color={"primary"}
                    onClick={(e) => openReservationTransfersForAgreementDialogInReadOnlyMode(e, rowData.transfer_agreement_id)}
                >
                <VisibilityIcon />
                </IconButton>
                : 
                rowData.transfer_status_id === TRANSFER_STATUS_KIND_PAID_CANCELED.id ? 
                <IconButton
                    color={"primary"}
                    onClick={(e) => handleClickOpenReturnTransfersForAgreementDialog(e, rowData.transfer_agreement_id)}
                >
                <VisibilityIcon />
                </IconButton>
                : 
                <IconButton
                    color={"primary"}
                    onClick={(e) => handleClickOpenReservationTransfersForAgreementDialogInEditMode(e, rowData.transfer_agreement_id)}
                >
                    <PaymentsIcon />
                </IconButton>
            ),
            // label: t("bankings.edit"),
            allowChangeVisibility: false,
            excludedFromExport: true,
        },
        {
            name: "passport",
            getValueCallback: (rowData) => rowData.transfer_agreement_id ? <AgreementPassportButton agreement={{id:rowData.transfer_agreement_id}} /> : null,
            label: t("bankings.agreement_passport"),
            getFilterFieldConfigCallback: () => undefined,
            sortable: false,
        },
    ];

    return isForInvocingPage ? fieldConfigs : fieldConfigs.filter(fieldConfig=>fieldConfig.name !== "transfer_date" && fieldConfig.name !== "transfer_is_invoiced")
};
